import { useState } from 'react';
import { DialogActions, Typography, Button, Box, styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import usePatchProcessedTimesheetStatus from '../../hooks/usePatchProcessedTimesheetStatus';
import { COLORS } from '../../../utils/Application_Constants';
import { useProcessedTimesheetContext } from '../../Contexts/ProcessedTimesheetContext';
import { requiresPtsCalculationValidation } from '../../Utils/validatePtsDetailCalculations';
import PtsDetailCalculationErrorsDisplay from './PtsDetailCalculationErrorsDisplay';
import useValidatePtsDetailCalculations from '../../hooks/useValidatePtsDetailCalculations';

const Textarea = styled('textarea')({
    width: '100%',
    height: '100px',
    resize: 'none',
    border: 'none',
    borderBottom: `1px solid ${COLORS.LT_MIDNIGHT10}`,
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '1rem',
    padding: '8px 0',
    overflow: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
});

function UpdateStatusReasonForm({
    id,
    onSuccess,
    lastModified,
    newStatus,
    onClose,
    processedTimesheetEntries = [],
    locations = [],
    onCalculationErrorsChange = () => {},
}) {
    const [error, setError] = useState('');
    const [statusChangeReason, setStatusChangeReason] = useState('');
    const queryClient = useQueryClient();
    const { handleOutdatedError } = useProcessedTimesheetContext();
    const { calculationErrors, isValidatingCalculations, validationError, validateCalculations } =
        useValidatePtsDetailCalculations({
            processedTimesheetId: id,
            processedTimesheetEntries,
            locations,
            onCalculationErrorsChange,
        });

    const { mutate: patchProcessedTimesheetStatus, isLoading: isPatchLoading } =
        usePatchProcessedTimesheetStatus({
            onSuccess: (updatedTimesheetData) => {
                onSuccess(updatedTimesheetData?.needsReviewStatus || updatedTimesheetData?.status);

                // Invalidate processed timesheets lists, processed timesheet details, and activity feed
                queryClient.invalidateQueries({ queryKey: ['processedTimesheets'] });
                queryClient.invalidateQueries({
                    queryKey: [
                        'processedTimesheetDetails',
                        { processedTimesheetId: id.toString() },
                    ],
                });
                queryClient.invalidateQueries({
                    queryKey: ['getActivityFeed', { processedTimesheetId: id.toString() }],
                });
            },
            onError: (apiError) => {
                handleOutdatedError(apiError, () =>
                    setError(
                        typeof apiError?.message === 'string' && apiError?.message
                            ? apiError.message
                            : 'There was an error changing status. Please try again.'
                    )
                );
            },
        });

    const handleSubmit = async () => {
        setError('');

        const isValid = await validateCalculations({
            shouldValidate: requiresPtsCalculationValidation(newStatus),
        });

        if (!isValid) {
            return;
        }

        patchProcessedTimesheetStatus({
            processedTimesheetId: id,
            newStatus,
            statusChangeReason,
            lastModified,
        });
    };

    return (
        <Box display="flex" flexDirection="column">
            <Typography
                sx={{
                    color: COLORS.BLACK,
                    fontSize: '16px',
                    fontWeight: '600',
                }}
                mb={2}
            >
                Reason for changing status
            </Typography>

            <Box mb={1}>
                <Textarea
                    placeholder="Add comment..."
                    value={statusChangeReason}
                    onChange={(e) => setStatusChangeReason(e.target.value)}
                    maxLength={800}
                />
                <Box display="flex" justifyContent="flex-start">
                    <Typography variant="caption" color={COLORS.LT_MI}>
                        {statusChangeReason.length}/800
                    </Typography>
                </Box>
            </Box>

            <PtsDetailCalculationErrorsDisplay errors={calculationErrors} />

            {error && (
                <Typography color="error" my={2}>
                    {error}
                </Typography>
            )}

            {validationError && (
                <Typography color="error" my={2}>
                    {validationError}
                </Typography>
            )}

            <DialogActions sx={{ justifyContent: 'flex-end', padding: 0 }}>
                <Button
                    disabled={isPatchLoading || isValidatingCalculations}
                    color="error"
                    onClick={onClose}
                    sx={{ p: '8px 10px', height: '33px' }}
                >
                    Cancel
                </Button>
                <Button
                    loading={isPatchLoading || isValidatingCalculations}
                    variant="contained"
                    color="primary"
                    sx={{
                        borderRadius: ({ shape }) => `${shape.borderRadiusVariants.md}px`,
                        p: '8px 10px',
                        width: '120px',
                        height: '33px',
                    }}
                    onClick={handleSubmit}
                >
                    Change status
                </Button>
            </DialogActions>
        </Box>
    );
}

export default UpdateStatusReasonForm;
